/**
 * Read-only broadcast WebSocket (`/ws/broadcast`). The token travels as the
 * sole Sec-WebSocket-Protocol value, never in the URL, and is never logged.
 * Reconnects with exponential backoff; close code 1008 = revoked / disabled /
 * invalid link (no retry); close code 4001 = the link went Off (EPIC-109), no
 * retry: the page falls back to polling /broadcast/status.
 */
import { useEffect, useRef, useState } from "react";
import { resolveBroadcastWsBaseUrl } from "../../lib/broadcastApi";
import type { BroadcastLayout, SceneTransition } from "../../lib/broadcastApi";

export type BroadcastSocketState =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "invalid_link"
  | "off";

export interface BroadcastSocketHandlers {
  onConfirmedEvent?: (raw: Record<string, any>) => void;
  onUndo?: (eventId: string) => void;
  onMatchUpdate?: () => void;
  onLayout?: (layout: BroadcastLayout, layoutSeq?: number) => void;
  /** `broadcast_take`: a new program scene plus its transition. */
  onTake?: (
    layout: BroadcastLayout,
    layoutSeq: number | undefined,
    transition: SceneTransition | undefined,
  ) => void;
  /** Control socket only: scene / take / on-air confirmations. */
  onControlMessage?: (type: "scene_updated" | "take" | "on_air") => void;
  onCallout?: (action: "dismiss" | "replay") => void;
  /** Fires on every (re)connect so the caller can re-fetch state. */
  onConnected?: () => void;
}

export const BACKOFF_BASE_MS = 1000;
export const BACKOFF_MAX_MS = 15000;
export const nextBackoff = (attempt: number) =>
  Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** Math.max(0, attempt));

export function useBroadcastSocket(
  token: string,
  handlers: BroadcastSocketHandlers,
  enabled = true,
  /** `/ws/broadcast` (default) or the control endpoint. */
  path = "/ws/broadcast",
): { state: BroadcastSocketState } {
  const [state, setState] = useState<BroadcastSocketState>("idle");
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!token || !enabled) {
      setState("idle");
      return;
    }
    let ws: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let stopped = false;

    const connect = (isReconnect: boolean) => {
      if (stopped) return;
      setState(isReconnect ? "reconnecting" : "connecting");
      ws = new WebSocket(`${resolveBroadcastWsBaseUrl()}${path}`, [token]);
      ws.onopen = () => {
        attempt = 0;
        setState("connected");
        handlersRef.current.onConnected?.();
      };
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          const h = handlersRef.current;
          if (data?.type === "broadcast_layout" && data.layout) {
            h.onLayout?.(
              data.layout as BroadcastLayout,
              typeof data.layout_seq === "number" ? data.layout_seq : undefined,
            );
          } else if (data?.type === "broadcast_take" && data.layout) {
            h.onTake?.(
              data.layout as BroadcastLayout,
              typeof data.layout_seq === "number" ? data.layout_seq : undefined,
              data.transition as SceneTransition | undefined,
            );
          } else if (
            data?.type === "scene_updated" ||
            data?.type === "take" ||
            data?.type === "on_air"
          ) {
            h.onControlMessage?.(data.type);
          } else if (data?.type === "broadcast_callout") {
            if (data.action === "dismiss" || data.action === "replay")
              h.onCallout?.(data.action);
          } else if (data?._confirmed && data.match_id) {
            h.onConfirmedEvent?.(data);
          } else if (data?.type === "event_undone" && data.event_id) {
            h.onUndo?.(String(data.event_id));
          } else if (
            data?.type === "match_state_changed" ||
            data?.type === "match_update"
          ) {
            h.onMatchUpdate?.();
          }
        } catch {
          // ignore malformed frames
        }
      };
      ws.onerror = () => ws?.close();
      ws.onclose = (ev) => {
        if (stopped) return;
        if (ev.code === 1008) {
          setState("invalid_link");
          return;
        }
        if (ev.code === 4001) {
          setState("off");
          return;
        }
        setState("reconnecting");
        timer = setTimeout(() => connect(true), nextBackoff(attempt));
        attempt += 1;
      };
    };

    connect(false);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      if (ws) {
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        ws.close();
      }
    };
  }, [token, enabled, path]);

  return { state };
}
